import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { StaffHeader } from "@/components/StaffHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ShieldCheck, AlertTriangle, Flag, Building2, Clock, Star, Pencil } from "lucide-react";

const CATS = [["UNSCHEDULED_CARE", "Soins non programmés"], ["MEDICAL_GUARD_CENTER", "Maison médicale de garde"], ["MEDICAL_CENTER", "Centre médical"], ["HOSPITAL_EMERGENCY", "Urgences hospitalières"], ["SOS_DOCTORS", "SOS Médecins"], ["PEDIATRIC_CARE", "Centre pédiatrique"], ["ON_CALL_PHARMACY", "Pharmacie de garde"]];
const ACCESS = ["WALK_IN", "APPOINTMENT", "PHONE_REGULATION", "EMERGENCY_REGULATION", "UNKNOWN"];
const DAYS = [["monday", "Lun"], ["tuesday", "Mar"], ["wednesday", "Mer"], ["thursday", "Jeu"], ["friday", "Ven"], ["saturday", "Sam"], ["sunday", "Dim"]];

const slotsToText = (arr) => (arr || []).map((s) => `${s[0]}-${s[1]}`).join(", ");
const textToSlots = (t) => (t || "").split(",").map((x) => x.trim()).filter(Boolean).map((x) => x.split("-").map((y) => y.trim())).filter((p) => p.length === 2);

const blank = () => ({
  name: "", category: "MEDICAL_CENTER", address: "", city: "", postalCode: "", phone: "",
  websiteUrl: "", bookingUrl: "", accessType: "UNKNOWN", callBeforeVisit: false, appointmentRequired: false,
  adultsAccepted: true, childrenAccepted: false, pmrAccessible: false,
  services: "", equipment: "", paymentMethods: "", parking: "", documentsToBring: "", informationSource: "",
  active: true, openingHours: {}, exceptionalClosures: "",
});

export default function CentresMedicauxAdmin() {
  const [list, setList] = useState([]);
  const [reports, setReports] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/admin/medical-centers").then((r) => setList(r.data.centers)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/admin/medical-centers/reports/all").then((r) => setReports(r.data.reports)).catch(() => {});
  };
  useEffect(load, []);

  const openNew = () => { setForm(blank()); setEditing("new"); };
  const openEdit = (c) => {
    setForm({
      ...blank(), ...c,
      services: (c.services || []).join(", "), equipment: (c.equipment || []).join(", "),
      paymentMethods: (c.paymentMethods || []).join(", "), documentsToBring: (c.documentsToBring || []).join(", "),
      exceptionalClosures: (c.exceptionalClosures || []).join(", "),
      openingHours: c.openingHours || {},
    });
    setEditing(c.id);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom obligatoire."); return; }
    setLoading(true);
    const payload = {
      ...form,
      services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
      equipment: form.equipment.split(",").map((s) => s.trim()).filter(Boolean),
      paymentMethods: form.paymentMethods.split(",").map((s) => s.trim()).filter(Boolean),
      documentsToBring: form.documentsToBring.split(",").map((s) => s.trim()).filter(Boolean),
      exceptionalClosures: form.exceptionalClosures.split(",").map((s) => s.trim()).filter(Boolean),
      latitude: form.lat ?? null, longitude: form.lng ?? null,
    };
    delete payload.id; delete payload.lat; delete payload.lng;
    try {
      if (editing === "new") await api.post("/admin/medical-centers", payload);
      else await api.put(`/admin/medical-centers/${editing}`, payload);
      toast.success("Fiche enregistrée."); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verify = async (id) => { await api.post(`/admin/medical-centers/${id}/verify`).catch(() => {}); toast.success("Fiche marquée comme vérifiée."); load(); };
  const toggleSponsor = async (c) => { await api.post(`/admin/medical-centers/${c.id}/sponsor`, { active: !c.sponsored, contractActive: !c.sponsored }).catch(() => {}); load(); };
  const setWait = async (c) => {
    const v = window.prompt("Minutes d'attente (vide = retirer) :", "");
    if (v === null) return;
    if (v.trim() === "") { await api.post(`/admin/medical-centers/${c.id}/waiting-time`, { minutes: null }); toast.success("Temps d'attente retiré."); load(); return; }
    const src = window.prompt("Source (obligatoire) :", "Déclaré par la structure");
    if (!src) { toast.error("Source obligatoire."); return; }
    try { await api.post(`/admin/medical-centers/${c.id}/waiting-time`, { minutes: parseInt(v, 10), source: src }); toast.success("Temps d'attente mis à jour."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resolveReport = async (id) => { await api.post(`/admin/medical-centers/reports/${id}/resolve`).catch(() => {}); load(); };

  const stale = (c) => c.needsVerification;
  const openReports = reports.filter((r) => r.status === "open");

  return (
    <div className="min-h-screen bg-slate-50">
      <StaffHeader />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl bg-emerald-600 grid place-items-center text-white"><Building2 className="h-5 w-5" /></div>
            <div><h1 className="font-heading text-2xl font-bold">Centres de soins — Administration</h1><p className="text-xs text-slate-500">{list.length} fiches · {openReports.length} signalement(s) ouvert(s)</p></div>
          </div>
          <Button data-testid="admin-new-center" onClick={openNew} className="rounded-xl gap-2 bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4" /> Nouvelle fiche</Button>
        </div>

        {openReports.length > 0 && (
          <div className="bg-white rounded-2xl border border-amber-200 p-4 mb-6" data-testid="admin-reports">
            <div className="font-semibold text-amber-800 flex items-center gap-2 mb-3"><Flag className="h-4 w-4" /> Signalements à traiter</div>
            <div className="space-y-2">
              {openReports.map((r) => (
                <div key={r.id} className="flex items-center gap-3 text-sm border-b border-slate-100 pb-2">
                  <div className="flex-1"><b>{r.centerName}</b> — {r.message} <span className="text-xs text-slate-400">({new Date(r.createdAt).toLocaleDateString("fr-FR")})</span></div>
                  <button data-testid={`resolve-${r.id}`} onClick={() => resolveReport(r.id)} className="text-emerald-700 font-semibold text-xs underline">Traiter</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-3" data-testid="admin-centers-list">
          {list.map((c) => (
            <div key={c.id} data-testid={`admin-center-${c.id}`} className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{c.name}</span>
                  {c.sponsored && <span className="text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5">Sponsorisé</span>}
                  {!c.active && <span className="text-[10px] font-bold bg-slate-100 text-slate-500 rounded px-1.5">Inactive</span>}
                  {stale(c) && <span className="text-[10px] font-bold bg-amber-100 text-amber-800 rounded px-1.5 flex items-center gap-0.5"><AlertTriangle className="h-3 w-3" /> à vérifier</span>}
                </div>
                <div className="text-xs text-slate-500">{c.categoryLabel} · {c.city || "—"} · vérifié {c.verifiedAt ? new Date(c.verifiedAt).toLocaleDateString("fr-FR") : "jamais"}</div>
              </div>
              <div className="flex gap-1.5">
                <button data-testid={`admin-edit-${c.id}`} onClick={() => openEdit(c)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Éditer</button>
                <button data-testid={`admin-verify-${c.id}`} onClick={() => verify(c.id)} className="h-9 px-3 rounded-lg border border-emerald-200 text-emerald-700 text-sm inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Vérifier</button>
                <button onClick={() => setWait(c)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Attente</button>
                <button onClick={() => toggleSponsor(c)} className={`h-9 px-3 rounded-lg border text-sm inline-flex items-center gap-1 ${c.sponsored ? "border-blue-300 text-blue-700" : "border-slate-200"}`}><Star className="h-3.5 w-3.5" /> Sponsor</button>
              </div>
            </div>
          ))}
        </div>
      </main>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "Nouvelle fiche" : "Modifier la fiche"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nom" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="f-name" />
            <div><Label className="text-sm">Catégorie</Label><select data-testid="f-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-11 rounded-xl border border-slate-200 px-3 mt-1.5">{CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <Field label="Adresse" v={form.address} on={(v) => setForm({ ...form, address: v })} testid="f-address" />
            <Field label="Ville" v={form.city} on={(v) => setForm({ ...form, city: v })} />
            <Field label="Code postal" v={form.postalCode} on={(v) => setForm({ ...form, postalCode: v })} />
            <Field label="Téléphone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} />
            <Field label="Site officiel (URL)" v={form.websiteUrl} on={(v) => setForm({ ...form, websiteUrl: v })} />
            <Field label="Lien de réservation (URL)" v={form.bookingUrl} on={(v) => setForm({ ...form, bookingUrl: v })} />
            <div><Label className="text-sm">Type d'accès</Label><select value={form.accessType} onChange={(e) => setForm({ ...form, accessType: e.target.value })} className="w-full h-11 rounded-xl border border-slate-200 px-3 mt-1.5">{ACCESS.map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
            <Field label="Services (séparés par ,)" v={form.services} on={(v) => setForm({ ...form, services: v })} />
            <Field label="Équipements (,)" v={form.equipment} on={(v) => setForm({ ...form, equipment: v })} />
            <Field label="Moyens de paiement (,)" v={form.paymentMethods} on={(v) => setForm({ ...form, paymentMethods: v })} />
            <Field label="Stationnement" v={form.parking} on={(v) => setForm({ ...form, parking: v })} />
            <Field label="Documents à prévoir (,)" v={form.documentsToBring} on={(v) => setForm({ ...form, documentsToBring: v })} />
            <Field label="Source des informations" v={form.informationSource} on={(v) => setForm({ ...form, informationSource: v })} />
            <Field label="Fermetures exceptionnelles (AAAA-MM-JJ, séparées par ,)" v={form.exceptionalClosures} on={(v) => setForm({ ...form, exceptionalClosures: v })} />
          </div>
          <div className="flex flex-wrap gap-3 mt-1">
            {[["callBeforeVisit", "Appeler avant"], ["appointmentRequired", "RDV requis"], ["adultsAccepted", "Adultes"], ["childrenAccepted", "Enfants"], ["pmrAccessible", "PMR"], ["active", "Active"]].map(([k, l]) => (
              <label key={k} className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} /> {l}</label>
            ))}
          </div>
          <div className="mt-2">
            <Label className="text-sm">Horaires (par jour, format 09:00-19:00, plusieurs plages séparées par ,)</Label>
            <div className="grid grid-cols-1 gap-1.5 mt-1.5">
              {DAYS.map(([k, l]) => (
                <div key={k} className="flex items-center gap-2"><span className="w-10 text-sm text-slate-500">{l}</span>
                  <Input value={slotsToText(form.openingHours[k])} onChange={(e) => setForm({ ...form, openingHours: { ...form.openingHours, [k]: textToSlots(e.target.value) } })} placeholder="Fermé" className="h-9 rounded-lg flex-1" /></div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="h-11 px-4 rounded-xl border border-slate-200 text-sm font-semibold">Annuler</button>
            <button data-testid="admin-save" onClick={save} disabled={loading} className="h-11 px-5 rounded-xl bg-emerald-600 text-white text-sm font-semibold">Enregistrer</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, v, on, testid }) => (
  <div><Label className="text-sm">{label}</Label><Input data-testid={testid} value={v || ""} onChange={(e) => on(e.target.value)} className="h-11 rounded-xl mt-1.5" /></div>
);
